import EditHeader from "common/components/EditHeader/EditHeader";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import useRouter from "common/hooks/use-router";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import usePark from "common/hooks/usePark";
import Button from "common/components/Button/Button";
import ImageComponent from "common/components/ImageComponent/ImageComponent";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import AnimalSelect from "components/select/AnimalSelect/AnimalSelect";
import { Animal } from "types/Animal";
import useAnimal from "common/hooks/useAnimal";
import TagSelect from "components/select/TagSelect/TagSelect";
// import { ObservationGrid } from "types/ObservationGrid";
import { Tag } from "types/Tag";
import { ObservationGroup } from "types/ObservationGroup";
import useAuth from "common/hooks/useAuth";
import EnclosureSelect from "components/select/EnclosureSelect/EnclosureSelect";
import { Enclosure } from "types/Enclosure";
import SpecySelect from "components/select/SpecySelect/SpecySelect";
import AnimalGenderSelect from "components/select/AnimalSelect/AnimalGenderSelect";
import getAnimalLabel from "utils/animalLabel";

type Props = {
  observationGroup: ObservationGroup;
  onSubmit: (value?: any) => any;
};

function ObservationGroupForm({ onSubmit, observationGroup }: Props) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const { navigate } = useRouter();
  const { user: currentUser } = useAuth();
  const { park: currentPark } = usePark();
  const { animals } = useAnimal();
  const canManageAnimalGroups = observationGroup?.id
    ? (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.["observation-group"]
            ?.update)) ??
      false
    : (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.["observation-group"]
            ?.create)) ??
      false;

  const initialValues = useMemo(
    () =>
      observationGroup?.id
        ? {
            ...observationGroup,
            park: observationGroup.park?.id ?? currentPark?.id,
            enclosures: observationGroup.enclosures?.map(
              (e: Enclosure) => e?.id
            ),
            animals: observationGroup.animals?.map((a: Animal) => a.id),
            tags: observationGroup?.tags?.map((tag: Tag) => tag.id),
            species: observationGroup?.species?.id ?? observationGroup?.species,
          }
        : {
            name: "",
            description: "",
            park: currentPark?.id,
          },
    [observationGroup, currentPark]
  );

  const additionalFilters = [
    {
      key: "enclosure",
      name: t("common.enclosures"),
      component: <EnclosureSelect />,
    },
    {
      key: "tags",
      name: t("forms.tags"),
      component: <TagSelect />,
    },
    {
      key: "gender",
      name: t("animals.gender"),
      component: <AnimalGenderSelect />,
    },
  ];

  const onSubmitWrapper = async (values: any) => {
    const newValues = { ...values };

    const res = await onSubmit(newValues);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  const removeAnimal = (form: any, values: any, deleteId: number) => {
    const newAnimals = values.animals.filter(
      (animalId: number) => animalId !== deleteId
    );
    form.mutators.setFieldValue({
      field: "animals",
      value: newAnimals,
    });
  };

  const onSpeciesChange = (value: any, form: any) => {
    form.mutators.setFieldValue({
      field: "species",
      value,
    });

    form.mutators.setFieldValue({
      field: "animals",
      value: null,
    });
  };

  return (
    <Form
      onSubmit={onSubmitWrapper}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field]: any, state: any, utils: any) => {
          utils.changeValue(state, field.field, () => field.value);
        },
      }}
      render={({ form, handleSubmit, values }) => {
        return (
          <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t(
                observationGroup?.id
                  ? "editItem.observationGroup"
                  : "newItem.observationGroup"
              )}
              subtitle={values.name}
              icon="FiEye"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={canManageAnimalGroups ? handleSubmit : undefined}
                  onCancel={() => navigate("/observation-groups")}
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div className="responsive-flex gap-2 mt-2">
                <div className="md:w-1/3 mb-2 md:mb-0">
                  <div className="white-box flex flex-col gap-2 w-full">
                    <div className="flex flex-col gap-2">
                      <FormControl
                        name="name"
                        title={t("common.name")}
                        required
                        editable={canManageAnimalGroups}
                      />
                    </div>
                    <FormControl
                      name="tags"
                      title={t("forms.tags")}
                      type="custom"
                      editable={canManageAnimalGroups}
                    >
                      <TagSelect multiple />
                    </FormControl>
                    <FormControl
                      name="enclosures"
                      title={t("forms.enclosures")}
                      type="custom"
                      editable={canManageAnimalGroups}
                    >
                      <EnclosureSelect multiple />
                    </FormControl>
                  </div>
                </div>
                <div className="md:w-2/3">
                  <div className="flex flex-col gap-2 w-full">
                    <div className="white-box flex flex-col gap-2 justify-between items-center ">
                      <div className="flex-1 w-full">
                        <FormControl
                          title={t("forms.species")}
                          type="custom"
                          isSelect
                          name="species"
                          editable={canManageAnimalGroups}
                          onChange={(value: any) =>
                            onSpeciesChange(value, form)
                          }
                        >
                          <SpecySelect />
                        </FormControl>
                      </div>
                      {values.species && (
                        <div className="flex-1 w-full">
                          <FormControl
                            title={t("forms.animals")}
                            type="custom"
                            isSelect
                            name="animals"
                            editable={canManageAnimalGroups}
                          >
                            <AnimalSelect
                              multiple
                              additionalFilters={additionalFilters}
                              specyFilter={{
                                name: "species",
                                value: values.species,
                              }}
                            />
                          </FormControl>
                        </div>
                      )}
                    </div>

                    {values?.animals?.map((animalId: number) => {
                      const animal = animals?.find(
                        (a: Animal) => a.id.toString() === animalId.toString()
                      );

                      return (
                        <div className="responsive-flex gap-4 items-center justify-start border bg-slate-50 border-slate-200 p-3 rounded-lg text-slate-700">
                          <div className="flex w-full gap-4">
                            {/* very left */}
                            <div className="flex relative shrink-0 mb-2 lg:mb-0 w-16 max-w-[4rem] h-16">
                              <div className="rounded-lg w-full h-full overflow-hidden  lg:w-auto">
                                <ImageComponent
                                  image={
                                    animal?.picture
                                      ? ImageFromStrapiMedia(animal?.picture)
                                          .uri
                                      : undefined
                                  }
                                />
                              </div>
                            </div>

                            {/* middle */}
                            <div className="flex flex-col xl:flex-row w-full">
                              <div className="flex flex-col flex-1">
                                <div className="flex flex-row align-center gap-3">
                                  <h4 className="text-xl font-bold text-primary">
                                    {getAnimalLabel(
                                      animal?.id,
                                      animal?.houseName,
                                      animal?.zimsId,
                                      animal?.localId,
                                      animal?.tag
                                    )}
                                  </h4>
                                  <div className="px-2 rounded-md bg-green-100 text-green-800 border border-green-500 flex flex-col justify-center items-center text-sm w-fit self-start">
                                    {t(`animals.${animal?.gender}`)}
                                  </div>
                                </div>
                                <div className="flex flex-col flex-1 justify-start mt-1">
                                  {animal?.species &&
                                  animal?.species.scientificName &&
                                  animal?.species.name ? (
                                    <div className="flex flex-row items-center text-sm gap-1">
                                      <span className="font-bold">
                                        {animal?.species.scientificName}
                                      </span>
                                      <span className="text-gray-400 italic">{`(${animal?.species.name})`}</span>
                                    </div>
                                  ) : animal?.species ? (
                                    <div className="flex flex-row items-center text-sm">
                                      <span className="font-bold">
                                        {animal?.species.scientificName ??
                                          animal?.species.name}
                                      </span>
                                    </div>
                                  ) : null}
                                </div>

                                {animal?.zimsId && (
                                  <div className="flex items-center gap-1 text-sm">
                                    <span className="font-bold">{`ID: ${animal?.zimsId}`}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="w-full md:w-auto flex justify-end">
                            <Button
                              type="error-line"
                              onClick={() =>
                                removeAnimal(form, values, animalId)
                              }
                              icon="TrashIcon"
                              className="px-3 py-3"
                              compact
                              disabled={!canManageAnimalGroups}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <HeaderButtonsEdit
                onSubmit={canManageAnimalGroups ? handleSubmit : undefined}
                onCancel={() => navigate("/observation-groups")}
              />
            </form>
          </div>
        );
      }}
    />
  );
}

export default ObservationGroupForm;
